import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import ActionButton from 'components/shared/actionButton';
import { ACTION_BUTTON_TYPES } from 'components/shared/actionButton/config';
import AlertDialog from 'components/shared/alertDialog';
import CustomButton from 'components/shared/customButton';
import CustomFormDrawer from 'components/shared/customFormDrawer';
import Filters from 'components/shared/filters';
import GridTable from 'components/shared/gridTable';
import NotificationCard from 'components/shared/notificationCard';
import PageContainer from 'components/shared/pageContainer';
import Search from 'components/shared/search';
import StatusIndicator from 'components/shared/statusIndicator';
import UserAvatar from 'components/shared/userAvatar';
import { sticky } from 'constants/helperCssRules';
import {
  APP_PAGES,
  PARAMS,
  SUCCESSION_POOLS_DEFAULT_PARAMS,
} from 'constants/pages';
import {
  API_SUCCESSION_TALENT_POOLS,
  api_succession_talent_pool,
  api_succession_talent_pool_archive,
} from 'constants/apiRoutes';
import { SUCCESSION_POOL_STATUS } from 'constants/succession';
import { successionPoolsSelector } from 'store/selectors/successionPoolsSelector';
import { successionPoolSelector } from 'store/selectors/successionPoolSelector';
import { peopleSelector } from 'store/selectors/peopleSelector';
import { getAllUsers } from 'store/modules/people';
import {
  getSuccessionPool,
  clearSuccessionPool,
} from 'store/modules/successionPool';
import {
  getSuccessionPools,
  clearSuccessionPools,
} from 'store/modules/successionPools';
import {
  prepareSuccessionNavigationItems,
  prepareSuccessionTableActionColumn,
} from 'utility/succession';
import http from 'utility/http';
import { isArray, isArrayEmpty, trimString } from 'utility/helpers';
import {
  hasNextPage,
  parseDuplicateParameters,
  showSuccessMessage,
  hasSelectedFiltersCheck,
} from 'utility/uiUtils';
import { useTranslations } from 'utility/useTranslations';
import {
  INITIAL_SUCCESSION_POOL_FILTERS,
  prepareSuccessionPoolFormFields,
  prepareSuccessionPoolFormInitialData,
  prepareSuccessionPoolValues,
  prepareSuccessionPoolFiltersFromLocation,
  prepareSuccessionPoolFilters,
  prepareSuccessionPoolTableHeaders,
  prepareSuccessionPoolTableRows,
} from './config';

const { SEARCH, PAGE } = PARAMS;
const { page: defaultPage } = SUCCESSION_POOLS_DEFAULT_PARAMS;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  stickyHeader: {
    padding: spacing(2, 0, 4, 0),
    ...sticky(primary.white, 170),
  },
  description: {
    marginBottom: spacing(6),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
  },
  toolbarEnd: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),
  },
  search: {
    width: 300,
    flexShrink: 0,
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  rowName: {
    color: primary.blue1,
    fontFamily: 'ProximaNova-Bold',
  },
  table: {
    '& .grid-table-header': {
      ...sticky(primary.bluish8, 276),
    },
    '& .actions-cell': {
      overflow: 'visible',
    },
    '& .actions-cell .row-cell-value': {
      overflow: 'visible',
    },
  },
}));

const SuccessionPoolsPage = ({ location, navigate, dispatch, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SUCCESSION_POOLS);
  const pools = useSelector(successionPoolsSelector);
  const { pool } = useSelector(successionPoolSelector);
  const { allUsers } = useSelector(peopleSelector);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(defaultPage);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(
    INITIAL_SUCCESSION_POOL_FILTERS
  );
  const [isPoolDrawerOpened, setIsPoolDrawerOpened] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const navigationItems = useMemo(
    () => prepareSuccessionNavigationItems(translations.navigationItems),
    [translations.navigationItems]
  );

  const filterLabels = useMemo(
    () => ({
      all: translations.all,
      status: translations.filters.labels.status,
      owner: translations.filters.labels.owner,
      active: translations.statuses.active,
      archived: translations.statuses.archived,
    }),
    [translations]
  );

  const confirmDialogTranslations = useMemo(() => {
    if (confirm) {
      if (confirm.type === 'delete') {
        return translations.deleteDialog;
      }

      return translations.archiveDialog;
    }

    return {};
  }, [translations, confirm]);

  const filters = useMemo(
    () => prepareSuccessionPoolFilters(filterLabels, allUsers),
    [filterLabels, allUsers]
  );

  const headers = useMemo(
    () => prepareSuccessionPoolTableHeaders(translations.columns),
    [translations.columns]
  );

  const actionColumn = useMemo(
    () => prepareSuccessionTableActionColumn(translations.columns.actions),
    [translations.columns.actions]
  );

  const poolFormInitialData = useMemo(
    () => prepareSuccessionPoolFormInitialData(pool),
    [pool]
  );

  const poolFormFields = useMemo(() => prepareSuccessionPoolFormFields(), []);

  const handlePageChange = useCallback(
    async ({
      newPage = page,
      newSearch = search,
      newFilters = selectedFilters,
      isLoadMore,
    } = {}) => {
      const params = {
        [PAGE]: newPage,
        ...newFilters,
        ...(newSearch ? { [SEARCH]: newSearch } : {}),
      };
      const query = parseDuplicateParameters(params);

      if (location.search !== `?${query}`) {
        navigate(`/succession/pools/?${query}`, { replace: true });
      }

      return getSuccessionPools(dispatch, isLoadMore, params);
    },
    [dispatch, location.search, navigate, page, search, selectedFilters]
  );

  const onSearch = async value => {
    const nextSearch = trimString(value);

    try {
      setPage(defaultPage);
      setSearch(nextSearch);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: nextSearch,
        newFilters: selectedFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onApplyFilters = async currentFilters => {
    try {
      setPage(defaultPage);
      setSelectedFilters(currentFilters);
      setIsLoading(true);
      await handlePageChange({
        newPage: defaultPage,
        newSearch: search,
        newFilters: currentFilters,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMore = () => {
    setPage(page + 1);
    handlePageChange({
      newPage: page + 1,
      newSearch: search,
      newFilters: selectedFilters,
      isLoadMore: true,
    });
  };

  const handleOpenPoolDrawer = useCallback(() => {
    setIsPoolDrawerOpened(true);
  }, []);

  const handleClosePoolDrawer = useCallback(() => {
    setIsPoolDrawerOpened(false);
    clearSuccessionPool(dispatch);
  }, [dispatch]);

  const handleEditPool = useCallback(
    currentPool => async () => {
      await getSuccessionPool(dispatch, currentPool.id);
      handleOpenPoolDrawer();
    },
    [dispatch, handleOpenPoolDrawer]
  );

  const openArchive = useCallback(currentPool => {
    setConfirm({ type: 'archive', pool: currentPool });
  }, []);

  const openDelete = useCallback(currentPool => {
    setConfirm({ type: 'delete', pool: currentPool });
  }, []);

  const handleSavePool = async values => {
    const payload = prepareSuccessionPoolValues(values);

    if (pool?.id) {
      await http.patch(api_succession_talent_pool(pool.id), payload);
      showSuccessMessage(translations.successMessages.updated);
    } else {
      await http.post(API_SUCCESSION_TALENT_POOLS, payload);
      showSuccessMessage(translations.successMessages.created);
    }

    handlePageChange({
      newPage: page,
      newSearch: search,
      newFilters: selectedFilters,
    });
  };

  const handleConfirm = async () => {
    if (confirm.type === 'delete') {
      await http.delete(api_succession_talent_pool(confirm.pool.id));
      showSuccessMessage(translations.successMessages.deleted);
    } else {
      await http.post(api_succession_talent_pool_archive(confirm.pool.id));
      showSuccessMessage(translations.successMessages.archived);
    }
    setConfirm(null);
    setPage(defaultPage);

    return handlePageChange({
      newPage: defaultPage,
      newSearch: search,
      newFilters: selectedFilters,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirm(null);
  };

  const renderUser = useCallback(
    user => <UserAvatar user={user} variant="subtitle1" caption small />,
    []
  );

  const renderStatus = useCallback(
    status => <StatusIndicator status={status} />,
    []
  );

  const renderActions = useCallback(
    currentPool => {
      const canArchive =
        !currentPool.can_delete &&
        currentPool.status === SUCCESSION_POOL_STATUS.ACTIVE;
      const canDelete = currentPool.can_delete;

      return (
        <div className={classes.rowActions}>
          <ActionButton
            type={ACTION_BUTTON_TYPES.EDIT}
            tooltipText={translations.menu.edit}
            onClickHandler={handleEditPool(currentPool)}
          />
          {canArchive ? (
            <ActionButton
              type={ACTION_BUTTON_TYPES.UNARCHIVE}
              tooltipText={translations.menu.archive}
              onClickHandler={() => openArchive(currentPool)}
            />
          ) : null}
          {canDelete ? (
            <ActionButton
              type={ACTION_BUTTON_TYPES.DELETE}
              tooltipText={translations.menu.delete}
              onClickHandler={() => openDelete(currentPool)}
            />
          ) : null}
        </div>
      );
    },
    [
      classes.rowActions,
      translations.menu,
      handleEditPool,
      openArchive,
      openDelete,
    ]
  );

  const rows = useMemo(
    () =>
      prepareSuccessionPoolTableRows(
        translations,
        pools.results,
        renderUser,
        renderActions,
        renderStatus
      ),
    [pools.results, translations, renderActions, renderUser, renderStatus]
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      const initial = prepareSuccessionPoolFiltersFromLocation(location.search);
      setPage(defaultPage);
      setSearch(initial.search);
      setSelectedFilters(initial.filters);
      try {
        await Promise.all([
          getAllUsers(dispatch),
          handlePageChange({
            newPage: defaultPage,
            newSearch: initial.search,
            newFilters: initial.filters,
          }),
        ]);
      } finally {
        if (active) setIsInitialLoading(false);
      }
    };

    load();
    return () => {
      active = false;
      dispatch(clearSuccessionPools());
      clearSuccessionPool(dispatch);
    };
    // Query string is applied once when the list opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRows = isArray(rows) && !isArrayEmpty(rows);
  const hasLoadMore = hasNextPage(pools);
  const isFiltered =
    Boolean(search) || hasSelectedFiltersCheck(selectedFilters);

  return (
    <PageContainer
      {...rest}
      location={location}
      navigate={navigate}
      dispatch={dispatch}
      translations={translations}
      navigationItems={navigationItems}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isInitialLoading && (
        <div>
          <div className={classes.stickyHeader}>
            <Typography className={classes.description} variant="body2">
              {translations.description}
            </Typography>
            <div className={classes.actions}>
              <Filters
                translations={translations.filters}
                selectedFilters={selectedFilters}
                filters={filters}
                onApplyFilters={onApplyFilters}
              />
              <div className={classes.toolbarEnd}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={onSearch}
                />
                <CustomButton
                  type="addDarkRoundedOutlined"
                  onClick={handleOpenPoolDrawer}
                >
                  {translations.createPool}
                </CustomButton>
              </div>
            </div>
          </div>
          {hasRows && (
            <GridTable
              className={classes.table}
              translations={translations}
              headers={headers}
              headerActions={actionColumn}
              rows={rows}
              hasLoadMore={hasLoadMore}
              onLoadMore={onLoadMore}
            />
          )}
          <NotificationCard
            title={isFiltered ? '' : translations.noResults.title}
            content={
              isFiltered
                ? translations.noSearchResultsMessage
                : translations.noResults.message
            }
            shouldFade={!isLoading && !hasRows}
            shouldFadeOutInstant
          />
        </div>
      )}
      <CustomFormDrawer
        translations={translations.poolForm}
        isOpened={isPoolDrawerOpened}
        isInitialValid={!!pool?.id}
        initialData={poolFormInitialData}
        fields={poolFormFields}
        employees={allUsers}
        onClose={handleClosePoolDrawer}
        onSave={handleSavePool}
        hideDelete
        hasCancelButton
        isMiddleWidth
      />
      <AlertDialog
        translations={confirmDialogTranslations}
        isOpened={Boolean(confirm)}
        isWarning={confirm?.type === 'delete'}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirm}
      />
    </PageContainer>
  );
};

SuccessionPoolsPage.propTypes = {
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SuccessionPoolsPage);
